import { Link } from "react-router-dom"
import {
  Target, ClipboardList, Users, GraduationCap, MapPin, BookOpen,
  Award, ClipboardCheck, CheckCircle2, ArrowRight, FileText, Building2,
} from "lucide-react"
import { SiteShell } from "../../components/site/SiteShell"
import { PageHero } from "../../components/site/PageHero"
import { Card, CardContent } from "../../components/ui/Card"
import { Button } from "../../components/ui/Button"
import { Badge } from "../../components/ui/Badge"
import { Alert } from "../../components/ui/Alert"

const objectives = [
  {
    icon: Target,
    title: "Bridge Theory & Practice",
    text: "Provide student-teachers with hands-on classroom experience that translates educational theory into effective teaching practice. Students apply principles of lesson planning, pedagogy and assessment in real school settings under expert guidance.",
  },
  {
    icon: Users,
    title: "Develop Professional Identity",
    text: "Cultivate the dispositions, ethics and habits of a professional teacher — punctuality, classroom management, learner-centred instruction and reflective practice — that distinguish YMS COE graduates in the field.",
  },
  {
    icon: GraduationCap,
    title: "Enhance Subject Mastery",
    text: "Reinforce the student-teacher's command of their teaching subjects through curriculum delivery, content adaptation and learner assessment across the basic and senior secondary school levels.",
  },
  {
    icon: ClipboardCheck,
    title: "Build Assessment Skills",
    text: "Equip students with practical experience in continuous assessment, examination setting, marking, grading and result interpretation in line with national standards and school policies.",
  },
  {
    icon: BookOpen,
    title: "Foster Community Engagement",
    text: "Encourage student-teachers to participate in school life beyond the classroom — clubs, pastoral care, parents' forums and extracurricular activities — deepening their understanding of the wider school community.",
  },
]

const processSteps = [
  { num: 1, title: "Orientation & Briefing", text: "Student-teachers attend a comprehensive orientation at the College covering expectations, code of conduct, lesson planning and supervision protocols." },
  { num: 2, title: "School Posting", text: "Students are posted to partner schools — basic, senior secondary or demonstration schools — based on their subject combinations and the availability of supervising teachers." },
  { num: 3, title: "Teaching Practice I", text: "In the first six weeks, student-teachers observe, assist and gradually take responsibility for teaching assigned classes under the mentorship of the school-based cooperating teacher." },
  { num: 4, title: "Teaching Practice II", text: "The next six weeks involve full classroom responsibility for assigned classes, including lesson delivery, assessment, record-keeping and co-curricular engagement." },
  { num: 5, title: "Supervision & Assessment", text: "College supervisors conduct at least three (3) unannounced and announced assessment visits per student, using a standardised rubric to evaluate planning, delivery and reflection." },
  { num: 6, title: "Evaluation & Reporting", text: "Final grades are computed from supervisor assessments (60%), cooperating teacher reports (25%) and student-teacher portfolios (15%). Results are published on the Student Portal." },
]

const requirements = [
  "Student must be in NCE 2 and have registered for the Teaching Practice course in the current session.",
  "A minimum CGPA of 1.5 in NCE 1 courses is required to proceed for Teaching Practice.",
  "Student must have completed the Teaching Practice orientation workshop organised by the School of Education.",
  "A completed Medical Fitness certificate from a recognised hospital must be submitted before posting.",
  "Student must possess the official Teaching Practice logbook and College identification card.",
  "Acceptance of the posting letter by the student and payment of the Teaching Practice supervision fee.",
]

const assessmentBreakdown = [
  { label: "College Supervisor Assessment", value: 60, desc: "Three supervision visits covering lesson planning, delivery, classroom management, assessment and professional conduct." },
  { label: "Cooperating Teacher Report", value: 25, desc: "Continuous assessment by the school-based mentor over the 12-week placement period." },
  { label: "Student-Teacher Portfolio", value: 15, desc: "Compiled logbook, lesson notes, reflective journal, assessment records and co-curricular evidence." },
]

export default function TeachingPractice() {
  return (
    <SiteShell>
      <PageHero
        breadcrumbs={[{ label: "Home", href: "/" }, { label: "Teaching Practice" }]}
        subtitle="Professional Experience"
        title="Teaching Practice: Where Theory Becomes Practice"
        description="Teaching Practice (TP) is the capstone professional experience of the NCE programme — a 12-week supervised school placement that transforms student-teachers into confident, capable educators ready to lead their own classrooms."
      />

      {/* Overview */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="relative">
              <div className="aspect-[4/3] rounded-2xl overflow-hidden shadow-xl">
                <img
                  src="https://images.unsplash.com/photo-1580582932707-520aed937b7b?q=80&w=1200&auto=format&fit=crop"
                  alt="Teaching practice"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -right-6 bg-brand-800 text-white p-6 rounded-2xl shadow-2xl hidden md:block">
                <ClipboardList className="w-8 h-8 text-gold-300 mb-2" />
                <div className="font-display text-3xl font-bold">12</div>
                <div className="text-xs uppercase tracking-wider opacity-80">Weeks Duration</div>
              </div>
            </div>
            <div>
              <Badge variant="success" className="mb-3">Overview</Badge>
              <h2 className="font-display text-3xl md:text-4xl font-bold text-ink-900 dark:text-ink-100 mb-5 leading-tight">
                A Defining Chapter in Every Teacher's Journey
              </h2>
              <p className="text-ink-600 dark:text-ink-300 leading-relaxed mb-4">
                Teaching Practice is undertaken by all NCE students in the second semester of NCE 2. It is a credit-bearing course that places student-teachers in real schools — basic, senior secondary or demonstration — for twelve weeks of supervised classroom teaching.
              </p>
              <p className="text-ink-600 dark:text-ink-300 leading-relaxed mb-4">
                During the placement, students are mentored by an experienced cooperating teacher at the host school and supervised by academic staff from the College. The experience bridges the gap between educational theory and the realities of classroom practice, allowing student-teachers to develop the confidence, competence and character expected of a professional educator.
              </p>
              <p className="text-ink-600 dark:text-ink-300 leading-relaxed">
                The College maintains partnerships with over 80 schools across Kano, Katsina and Jigawa States, ensuring every student-teacher has access to a quality placement environment appropriate to their subject combination.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Objectives */}
      <section className="py-16 bg-white dark:bg-ink-900">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <Badge variant="warning" className="mb-3">Objectives</Badge>
            <h2 className="font-display text-3xl md:text-4xl font-bold text-ink-900 dark:text-ink-100">What Teaching Practice Achieves</h2>
            <p className="text-ink-500 dark:text-ink-400 mt-3 max-w-2xl mx-auto">
              Five core objectives define the goals of every Teaching Practice placement at YMS COE.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {objectives.map(o => (
              <Card key={o.title} className="card-lift border-0 shadow-md h-full">
                <CardContent className="pt-6">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-brand-600 to-brand-800 grid place-items-center mb-4 shadow-md">
                    <o.icon className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="font-display font-bold text-base text-ink-900 dark:text-ink-100 mb-2">{o.title}</h3>
                  <p className="text-sm text-ink-600 dark:text-ink-400 leading-relaxed">{o.text}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Process timeline */}
      <section className="py-20 bg-brand-950 dark:bg-ink-950 text-white relative overflow-hidden">
        <div className="absolute inset-0 pattern-dots opacity-20" />
        <div className="container mx-auto px-4 relative">
          <div className="text-center mb-12">
            <Badge variant="warning" className="mb-3">The Process</Badge>
            <h2 className="font-display text-3xl md:text-4xl font-bold">6-Stage Teaching Practice Journey</h2>
            <p className="text-white/70 mt-3 max-w-2xl mx-auto">From orientation to final evaluation, here is how the 12-week placement unfolds.</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {processSteps.map(s => (
              <div key={s.num} className="bg-white/5 backdrop-blur border border-white/10 rounded-xl p-6 hover:bg-white/10 transition-colors relative">
                <div className="absolute -top-3 -left-3 w-10 h-10 rounded-full bg-gold-500 text-ink-950 grid place-items-center font-display font-bold text-lg shadow-lg">
                  {s.num}
                </div>
                <h3 className="font-display font-bold text-lg mb-2 mt-2">{s.title}</h3>
                <p className="text-sm text-white/75 leading-relaxed">{s.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Requirements */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-10 items-start">
            <div>
              <Badge variant="success" className="mb-3">Before You Begin</Badge>
              <h2 className="font-display text-3xl md:text-4xl font-bold text-ink-900 dark:text-ink-100 mb-5 leading-tight">
                Eligibility & Requirements
              </h2>
              <p className="text-ink-600 dark:text-ink-300 leading-relaxed mb-6">
                To be eligible for Teaching Practice, student-teachers must satisfy all of the following requirements. Failure to meet any criterion may result in deferment to the next academic session.
              </p>
              <ul className="space-y-3">
                {requirements.map((r, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm text-ink-700 dark:text-ink-300 leading-relaxed">
                    <CheckCircle2 className="w-5 h-5 text-brand-600 dark:text-brand-400 mt-0.5 shrink-0" />
                    <span>{r}</span>
                  </li>
                ))}
              </ul>
            </div>
            <Card className="border-0 shadow-md">
              <CardContent className="pt-6">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-12 h-12 rounded-lg bg-gold-50 dark:bg-gold-900/30 grid place-items-center">
                    <Award className="w-6 h-6 text-gold-700 dark:text-gold-400" />
                  </div>
                  <div>
                    <h3 className="font-display font-bold text-lg text-ink-900 dark:text-ink-100">Assessment Breakdown</h3>
                    <p className="text-xs text-ink-500 dark:text-ink-400">Final TP grade composition</p>
                  </div>
                </div>
                <div className="space-y-5">
                  {assessmentBreakdown.map(a => (
                    <div key={a.label}>
                      <div className="flex items-center justify-between mb-1.5">
                        <span className="text-sm font-semibold text-ink-900 dark:text-ink-100">{a.label}</span>
                        <span className="text-sm font-bold text-brand-700 dark:text-brand-400">{a.value}%</span>
                      </div>
                      <div className="h-2 bg-ink-100 dark:bg-ink-800 rounded-full overflow-hidden mb-2">
                        <div className="h-full bg-gradient-to-r from-brand-600 to-brand-800 rounded-full" style={{ width: `${a.value}%` }} />
                      </div>
                      <p className="text-xs text-ink-500 dark:text-ink-400 leading-relaxed">{a.desc}</p>
                    </div>
                  ))}
                </div>
                <Alert variant="info" className="mt-6">
                  <div className="text-xs">
                    <strong className="block mb-1">Pass Mark:</strong> A minimum overall score of 50% is required to pass Teaching Practice. Students who fail must repeat the placement in the next academic session.
                  </div>
                </Alert>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Resources */}
      <section className="py-16 bg-white dark:bg-ink-900">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-6">
            <Link to="/downloads" className="group">
              <Card className="border-0 shadow-md hover:shadow-lg transition-shadow h-full">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="w-12 h-12 rounded-lg bg-brand-50 dark:bg-brand-900/40 grid place-items-center">
                      <FileText className="w-6 h-6 text-brand-700 dark:text-brand-400" />
                    </div>
                    <ArrowRight className="w-5 h-5 text-ink-400 group-hover:text-brand-600 group-hover:translate-x-1 transition-all" />
                  </div>
                  <h3 className="font-display font-bold text-xl text-ink-900 dark:text-ink-100 mb-2">TP Resources & Forms</h3>
                  <p className="text-sm text-ink-600 dark:text-ink-400 leading-relaxed">
                    Download the Teaching Practice logbook, lesson plan templates, supervision forms and the official handbook from the Downloads page.
                  </p>
                </CardContent>
              </Card>
            </Link>
            <Link to="/programs" className="group">
              <Card className="border-0 shadow-md hover:shadow-lg transition-shadow h-full">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="w-12 h-12 rounded-lg bg-gold-50 dark:bg-gold-900/30 grid place-items-center">
                      <Building2 className="w-6 h-6 text-gold-700 dark:text-gold-400" />
                    </div>
                    <ArrowRight className="w-5 h-5 text-ink-400 group-hover:text-gold-600 group-hover:translate-x-1 transition-all" />
                  </div>
                  <h3 className="font-display font-bold text-xl text-ink-900 dark:text-ink-100 mb-2">Partner Schools</h3>
                  <p className="text-sm text-ink-600 dark:text-ink-400 leading-relaxed">
                    The College partners with over 80 schools across three states. Learn more about our programmes and how we prepare students for placement.
                  </p>
                </CardContent>
              </Card>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <Card className="border-0 shadow-lg bg-gradient-to-br from-brand-800 to-brand-950 text-white">
            <CardContent className="pt-8 text-center">
              <MapPin className="w-10 h-10 text-gold-300 mx-auto mb-3" />
              <h2 className="font-display text-2xl md:text-3xl font-bold mb-3">Questions About Teaching Practice?</h2>
              <p className="text-white/80 mb-6 max-w-2xl mx-auto">
                The Teaching Practice Unit in the School of Education is available to answer all enquiries from students, partner schools and prospective supervisors.
              </p>
              <Link to="/contact">
                <Button size="lg" className="bg-gold-500 hover:bg-gold-600 text-ink-950">
                  Contact the TP Unit <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </CardContent>
          </Card>
        </div>
      </section>
    </SiteShell>
  )
}
